import { useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Star,
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  ChevronRight,
  Trash2,
  Calendar,
  MessageSquare,
  MoreVertical,
  Users,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  X,
  Filter,
  ChevronDown,
  RotateCcw,
  ExternalLink,
  Download,
} from 'lucide-react'
import * as XLSX from 'xlsx'
import {
  useShortlist,
  useRemoveFromShortlist,
  useUpdateShortlistItem,
  SHORTLIST_STATUS_LABELS,
  SHORTLIST_STATUS_COLORS,
  type ShortlistStatus,
  type ShortlistItem,
} from '@/hooks/useShortlist'
import { usePageHeader } from '@/contexts/PageHeaderContext'
import { cn } from '@/lib/utils'

export function ShortlistPage() {
  const navigate = useNavigate()
  const { data: shortlist = [], isLoading } = useShortlist()
  const removeFromShortlist = useRemoveFromShortlist()
  const updateShortlistItem = useUpdateShortlistItem()
  const { setHeaderContent } = usePageHeader()

  const [editingNotes, setEditingNotes] = useState<string | null>(null)
  const [notesValue, setNotesValue] = useState('')
  const [editingAction, setEditingAction] = useState<string | null>(null)
  const [actionValue, setActionValue] = useState('')
  const [actionDate, setActionDate] = useState('')
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<ShortlistStatus | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [countryFilter, setCountryFilter] = useState('')
  const [employeeFilter, setEmployeeFilter] = useState('')
  const [revenueFilter, setRevenueFilter] = useState('')

  // Extract unique values for filters from shortlist data
  const filterOptions = useMemo(() => {
    const countries = new Set<string>()
    const employeeRanges = new Set<string>()
    const revenueRanges = new Set<string>()

    shortlist.forEach(item => {
      if (item.lead?.country) countries.add(item.lead.country)
      if (item.lead?.employee_range) employeeRanges.add(item.lead.employee_range)
      if (item.lead?.revenue_range) revenueRanges.add(item.lead.revenue_range)
    })

    return {
      countries: Array.from(countries).sort(),
      employeeRanges: Array.from(employeeRanges),
      revenueRanges: Array.from(revenueRanges),
    }
  }, [shortlist])

  // Filter shortlist by all filters
  const filteredShortlist = useMemo(() => {
    return shortlist.filter(item => {
      // Status filter
      if (filterStatus !== 'all' && item.status !== filterStatus) return false

      // Country filter
      if (countryFilter && item.lead?.country !== countryFilter) return false

      // Employee range filter
      if (employeeFilter && item.lead?.employee_range !== employeeFilter) return false

      // Revenue range filter
      if (revenueFilter && item.lead?.revenue_range !== revenueFilter) return false

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const name = item.lead?.name?.toLowerCase() || ''
        const city = item.lead?.city?.toLowerCase() || ''
        const country = item.lead?.country?.toLowerCase() || ''
        const industry = item.lead?.industry?.toLowerCase() || ''
        const notes = item.notes?.toLowerCase() || ''

        return name.includes(query) ||
               city.includes(query) ||
               country.includes(query) ||
               industry.includes(query) ||
               notes.includes(query)
      }

      return true
    })
  }, [shortlist, filterStatus, countryFilter, employeeFilter, revenueFilter, searchQuery])

  const activeFilterCount = [countryFilter, employeeFilter, revenueFilter].filter(Boolean).length
  const hasActiveFilters = activeFilterCount > 0

  const clearFilters = () => {
    setCountryFilter('')
    setEmployeeFilter('')
    setRevenueFilter('')
  }

  // Group by status for summary
  const statusCounts = shortlist.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1
    return acc
  }, {} as Record<ShortlistStatus, number>)

  const handleRemove = async (leadId: string) => {
    if (confirm('Remove from shortlist?')) {
      await removeFromShortlist.mutateAsync(leadId)
    }
    setActiveMenu(null)
  }

  const handleStatusChange = async (id: string, status: ShortlistStatus) => {
    await updateShortlistItem.mutateAsync({ id, status })
    setActiveMenu(null)
  }

  const handleSaveNotes = async (id: string) => {
    await updateShortlistItem.mutateAsync({ id, notes: notesValue })
    setEditingNotes(null)
    setNotesValue('')
  }

  const handleSaveAction = async (id: string) => {
    await updateShortlistItem.mutateAsync({
      id,
      next_action: actionValue,
      next_action_date: actionDate || null,
    })
    setEditingAction(null)
    setActionValue('')
    setActionDate('')
  }

  const startEditNotes = (item: ShortlistItem) => {
    setEditingNotes(item.id)
    setNotesValue(item.notes || '')
  }

  const startEditAction = (item: ShortlistItem) => {
    setEditingAction(item.id)
    setActionValue(item.next_action || '')
    setActionDate(item.next_action_date || '')
  }

  const formatRevenue = (revenue: number) => {
    if (revenue >= 1000000000) return `$${(revenue / 1000000000).toFixed(1)}B`
    if (revenue >= 1000000) return `$${(revenue / 1000000).toFixed(1)}M`
    if (revenue >= 1000) return `$${(revenue / 1000).toFixed(0)}K`
    return `$${revenue}`
  }

  const exportShortlistToExcel = useCallback(() => {
    const data = shortlist
      .filter(item => item.lead != null)
      .map(item => {
        const lead = item.lead!
        return {
          'Company Name': lead.name || '',
          'Status': SHORTLIST_STATUS_LABELS[item.status] || item.status,
          'Country': lead.country || '',
          'City': lead.city || '',
          'Website': lead.website || '',
          'Email': lead.email || '',
          'Phone': lead.phone || '',
          'Industry': lead.industry || '',
          'Employees': lead.employee_range || '',
          'Revenue': lead.revenue_range || '',
          'Services': lead.services?.filter(Boolean).join(', ') || '',
          'Priority': item.priority ?? '',
          'Notes': item.notes || '',
          'Next Action': item.next_action || '',
          'Next Action Date': item.next_action_date || '',
          'Added': item.created_at ? new Date(item.created_at).toLocaleDateString() : '',
        }
      })

    const ws = XLSX.utils.json_to_sheet(data)
    const colWidths = Object.keys(data[0] || {}).map(key => ({
      wch: Math.max(
        key.length,
        ...data.map(row => String(row[key as keyof typeof row] ?? '').length)
      )
    }))
    ws['!cols'] = colWidths

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Shortlist')
    XLSX.writeFile(wb, `shortlist_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }, [shortlist])

  const getStatusIcon = (status: ShortlistStatus) => {
    switch (status) {
      case 'converted':
        return <CheckCircle className="w-4 h-4" />
      case 'dropped':
        return <XCircle className="w-4 h-4" />
      case 'meeting_scheduled':
      case 'proposal_sent':
      case 'negotiating':
        return <Clock className="w-4 h-4" />
      default:
        return <AlertCircle className="w-4 h-4" />
    }
  }

  const getLeadUrl = (leadId: string) => `/admin/leads/${leadId}?from=shortlist`

  const handleLeadClick = (leadId: string, e?: React.MouseEvent) => {
    const url = getLeadUrl(leadId)
    // Open in new tab if Ctrl/Cmd+click or middle-click
    if (e && (e.ctrlKey || e.metaKey || e.button === 1)) {
      window.open(url, '_blank')
      return
    }
    navigate(url)
  }

  const openInNewTab = (e: React.MouseEvent, leadId: string) => {
    e.stopPropagation()
    window.open(getLeadUrl(leadId), '_blank')
  }

  // Set header content for navbar (desktop only)
  useEffect(() => {
    setHeaderContent(
      <div className="flex items-center gap-2 w-full">
        {/* Title */}
        <div className="flex items-center gap-2 mr-auto">
          <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
          <h1 className="text-lg font-bold text-secondary-900">Shortlist</h1>
          <span className="text-secondary-400">|</span>
          <p className="text-secondary-500 text-sm">{shortlist.length} tracked</p>
        </div>

        {/* Country Filter */}
        <div className="relative min-w-[140px]">
          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="w-full appearance-none pl-3 pr-7 py-1.5 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
          >
            <option value="">All Countries</option>
            {filterOptions.countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-secondary-400 pointer-events-none" />
        </div>

        {/* Search */}
        <div className="relative w-44">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-1.5 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Export */}
        {shortlist.length > 0 && (
          <button
            onClick={exportShortlistToExcel}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 text-sm font-medium transition-colors"
            title="Export shortlist to Excel"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        )}

        {/* Filter Button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-sm font-medium transition-colors',
            showFilters || hasActiveFilters
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-secondary-300 text-secondary-700 hover:bg-secondary-50'
          )}
        >
          <Filter className="w-4 h-4" />
          {hasActiveFilters && (
            <span className="bg-primary-600 text-white text-xs px-1.5 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>
    )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortlist.length, countryFilter, filterOptions.countries.length, searchQuery, showFilters, hasActiveFilters, activeFilterCount, exportShortlistToExcel])

  // Cleanup header on unmount
  useEffect(() => {
    return () => setHeaderContent(null)
  }, [setHeaderContent])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-secondary-200 px-4 py-2">
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
          <h1 className="text-base font-bold text-secondary-900">Shortlist</h1>
          <span className="text-secondary-400">|</span>
          <span className="text-secondary-500 text-sm">{shortlist.length}</span>
          <div className="ml-auto flex items-center gap-2">
            {shortlist.length > 0 && (
              <button
                onClick={exportShortlistToExcel}
                className="p-1.5 rounded-lg border border-green-300 bg-green-50 text-green-700"
                title="Export to Excel"
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                'p-1.5 rounded-lg border',
                showFilters || hasActiveFilters
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-secondary-300'
              )}
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        <div className="mt-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-1.5 border border-secondary-300 rounded-lg text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-secondary-200">
            <div className="flex flex-wrap items-center gap-3">
              {/* Country - Only show on mobile (hidden on lg+) */}
              <div className="lg:hidden relative min-w-[140px]">
                <select
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  className="w-full appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">All Countries</option>
                  {filterOptions.countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
              </div>

              {/* Employees */}
              {filterOptions.employeeRanges.length > 0 && (
                <div className="relative min-w-[130px]">
                  <select
                    value={employeeFilter}
                    onChange={(e) => setEmployeeFilter(e.target.value)}
                    className="w-full appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                  >
                    <option value="">All Employees</option>
                    {filterOptions.employeeRanges.map((range) => (
                      <option key={range} value={range}>
                        {range}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
                </div>
              )}

              {/* Revenue */}
              {filterOptions.revenueRanges.length > 0 && (
                <div className="relative min-w-[130px]">
                  <select
                    value={revenueFilter}
                    onChange={(e) => setRevenueFilter(e.target.value)}
                    className="w-full appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                  >
                    <option value="">All Revenue</option>
                    {filterOptions.revenueRanges.map((range) => (
                      <option key={range} value={range}>
                        {range}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
                </div>
              )}

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Desktop Filter Panel */}
      {showFilters && (
        <div className="hidden lg:block bg-white border-b border-secondary-200 px-6 py-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Employees */}
            {filterOptions.employeeRanges.length > 0 && (
              <div className="relative">
                <select
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                  className="appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">All Employees</option>
                  {filterOptions.employeeRanges.map((range) => (
                    <option key={range} value={range}>
                      {range}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
              </div>
            )}

            {/* Revenue */}
            {filterOptions.revenueRanges.length > 0 && (
              <div className="relative">
                <select
                  value={revenueFilter}
                  onChange={(e) => setRevenueFilter(e.target.value)}
                  className="appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">All Revenue</option>
                  {filterOptions.revenueRanges.map((range) => (
                    <option key={range} value={range}>
                      {range}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
              </div>
            )}

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset All
              </button>
            )}
          </div>
        </div>
      )}

      {/* Status Summary - Always visible */}
      <div className="bg-white border-b border-secondary-200 px-4 lg:px-6 py-3">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
              filterStatus === 'all'
                ? 'bg-secondary-900 text-white'
                : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
            )}
          >
            All ({shortlist.length})
          </button>
          {(Object.keys(SHORTLIST_STATUS_LABELS) as ShortlistStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={cn(
                'px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5',
                filterStatus === status
                  ? 'text-white'
                  : 'text-secondary-600 hover:opacity-80'
              )}
              style={{
                backgroundColor: filterStatus === status
                  ? SHORTLIST_STATUS_COLORS[status]
                  : `${SHORTLIST_STATUS_COLORS[status]}20`,
                color: filterStatus === status ? 'white' : SHORTLIST_STATUS_COLORS[status],
              }}
            >
              {getStatusIcon(status)}
              {SHORTLIST_STATUS_LABELS[status]}
              {statusCounts[status] ? ` (${statusCounts[status]})` : ''}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {filteredShortlist.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-secondary-200">
            <Star className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900">
              {filterStatus === 'all' ? 'No companies shortlisted yet' : `No ${SHORTLIST_STATUS_LABELS[filterStatus].toLowerCase()} companies`}
            </h3>
            <p className="text-secondary-600 mt-1">
              {filterStatus === 'all'
                ? 'Add companies from the Sales Leads page to track them here'
                : 'Change the status of companies to see them here'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredShortlist.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl border border-secondary-200 hover:shadow-md transition-shadow"
              >
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Image */}
                    <div className="w-20 h-20 bg-secondary-100 rounded-lg flex-shrink-0 overflow-hidden">
                      {item.lead?.image_url ? (
                        <img
                          src={item.lead.image_url}
                          alt={item.lead?.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building2 className="w-8 h-8 text-secondary-300" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => handleLeadClick(item.lead_id, e)}
                              onAuxClick={(e) => handleLeadClick(item.lead_id, e)}
                              className="font-semibold text-secondary-900 hover:text-primary-600 transition-colors text-left"
                            >
                              {item.lead?.name}
                            </button>
                            <button
                              onClick={(e) => openInNewTab(e, item.lead_id)}
                              className="p-1 hover:bg-secondary-100 rounded transition-colors opacity-50 hover:opacity-100"
                              title="Open in new tab"
                            >
                              <ExternalLink className="w-3.5 h-3.5 text-secondary-500" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-secondary-500 mt-0.5">
                            <MapPin className="w-3.5 h-3.5" />
                            <span>{item.lead?.city ? `${item.lead.city}, ` : ''}{item.lead?.country}</span>
                          </div>
                        </div>

                        {/* Status Badge & Actions */}
                        <div className="flex items-center gap-2">
                          <span
                            className="px-2.5 py-1 rounded-full text-xs font-medium text-white flex items-center gap-1"
                            style={{ backgroundColor: SHORTLIST_STATUS_COLORS[item.status] }}
                          >
                            {getStatusIcon(item.status)}
                            {SHORTLIST_STATUS_LABELS[item.status]}
                          </span>

                          {/* Actions Menu */}
                          <div className="relative">
                            <button
                              onClick={() => setActiveMenu(activeMenu === item.id ? null : item.id)}
                              className="p-1.5 hover:bg-secondary-100 rounded-lg transition-colors"
                            >
                              <MoreVertical className="w-4 h-4 text-secondary-500" />
                            </button>

                            {activeMenu === item.id && (
                              <>
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setActiveMenu(null)}
                                />
                                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-secondary-200 py-1 z-20">
                                  <div className="px-3 py-1.5 text-xs font-medium text-secondary-500 uppercase">
                                    Change Status
                                  </div>
                                  {(Object.keys(SHORTLIST_STATUS_LABELS) as ShortlistStatus[]).map((status) => (
                                    <button
                                      key={status}
                                      onClick={() => handleStatusChange(item.id, status)}
                                      className={cn(
                                        'w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-secondary-50',
                                        item.status === status && 'bg-secondary-50'
                                      )}
                                    >
                                      <span
                                        className="w-2 h-2 rounded-full"
                                        style={{ backgroundColor: SHORTLIST_STATUS_COLORS[status] }}
                                      />
                                      {SHORTLIST_STATUS_LABELS[status]}
                                    </button>
                                  ))}
                                  <div className="border-t border-secondary-200 mt-1 pt-1">
                                    <button
                                      onClick={() => handleRemove(item.lead_id)}
                                      className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 text-red-600 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      Remove from Shortlist
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Contact & Details Row */}
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                        {item.lead?.industry && (
                          <span className="text-secondary-600">{item.lead.industry}</span>
                        )}
                        {item.lead?.employee_range && (
                          <span className="flex items-center gap-1 text-secondary-500">
                            <Users className="w-3.5 h-3.5" />
                            {item.lead.employee_range}
                          </span>
                        )}
                        {item.lead?.revenue && (
                          <span className="flex items-center gap-1 text-secondary-500">
                            <DollarSign className="w-3.5 h-3.5" />
                            {formatRevenue(item.lead.revenue)}
                          </span>
                        )}
                        {item.lead?.website && (
                          <a
                            href={item.lead.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-primary-600 hover:text-primary-700"
                          >
                            <Globe className="w-3.5 h-3.5" />
                            Website
                          </a>
                        )}
                        {item.lead?.email && (
                          <a
                            href={`mailto:${item.lead.email}`}
                            className="flex items-center gap-1 text-secondary-500 hover:text-secondary-700"
                          >
                            <Mail className="w-3.5 h-3.5" />
                            {item.lead.email}
                          </a>
                        )}
                        {item.lead?.phone && (
                          <a
                            href={`tel:${item.lead.phone}`}
                            className="flex items-center gap-1 text-secondary-500 hover:text-secondary-700"
                          >
                            <Phone className="w-3.5 h-3.5" />
                            {item.lead.phone}
                          </a>
                        )}
                      </div>
                    </div>

                    {/* View Button */}
                    <button
                      onClick={(e) => handleLeadClick(item.lead_id, e)}
                      onAuxClick={(e) => handleLeadClick(item.lead_id, e)}
                      className="p-2 hover:bg-secondary-100 rounded-lg transition-colors flex-shrink-0"
                    >
                      <ChevronRight className="w-5 h-5 text-secondary-400" />
                    </button>
                  </div>

                  {/* Notes & Next Action */}
                  <div className="mt-4 pt-4 border-t border-secondary-100 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Notes */}
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium text-secondary-700 mb-2">
                        <MessageSquare className="w-4 h-4" />
                        Notes
                      </div>
                      {editingNotes === item.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={notesValue}
                            onChange={(e) => setNotesValue(e.target.value)}
                            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            rows={3}
                            placeholder="Add notes about this lead..."
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveNotes(item.id)}
                              className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingNotes(null)}
                              className="px-3 py-1.5 text-secondary-600 text-sm hover:bg-secondary-100 rounded-lg"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditNotes(item)}
                          className="w-full text-left p-2 text-sm text-secondary-500 bg-secondary-50 hover:bg-secondary-100 rounded-lg transition-colors"
                        >
                          {item.notes || 'Click to add notes...'}
                        </button>
                      )}
                    </div>

                    {/* Next Action */}
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium text-secondary-700 mb-2">
                        <Calendar className="w-4 h-4" />
                        Next Action
                      </div>
                      {editingAction === item.id ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={actionValue}
                            onChange={(e) => setActionValue(e.target.value)}
                            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Follow up call, Send proposal..."
                            autoFocus
                          />
                          <input
                            type="date"
                            value={actionDate}
                            onChange={(e) => setActionDate(e.target.value)}
                            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveAction(item.id)}
                              className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingAction(null)}
                              className="px-3 py-1.5 text-secondary-600 text-sm hover:bg-secondary-100 rounded-lg"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditAction(item)}
                          className="w-full text-left p-2 text-sm bg-secondary-50 hover:bg-secondary-100 rounded-lg transition-colors"
                        >
                          {item.next_action ? (
                            <div className="flex items-center justify-between">
                              <span className="text-secondary-700">{item.next_action}</span>
                              {item.next_action_date && (
                                <span className="text-secondary-500">
                                  {new Date(item.next_action_date).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-secondary-500">Click to add next action...</span>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
